/**
 * Cache utility for Supabase data
 * Uses sessionStorage for fast, temporary caching
 */

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

interface CacheItem<T> {
  data: T
  timestamp: number
}

export const cache = {
  /**
   * Get cached data if it exists and is not expired
   */
  get: <T,>(key: string): T | null => {
    try {
      const cached = sessionStorage.getItem(key)
      if (!cached) return null

      const item: CacheItem<T> = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is expired
      if (now - item.timestamp > CACHE_DURATION) {
        sessionStorage.removeItem(key)
        return null
      }

      return item.data
    } catch (error) {
      console.error('Cache get error:', error)
      return null
    }
  },

  /**
   * Set data in cache with current timestamp
   */
  set: <T,>(key: string, data: T): void => {
    try {
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
      }
      sessionStorage.setItem(key, JSON.stringify(item))
    } catch (error) {
      console.error('Cache set error:', error)
    }
  },

  /**
   * Clear specific cache key
   */
  clear: (key: string): void => {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.error('Cache clear error:', error)
    }
  },

  /**
   * Clear all cache
   */
  clearAll: (): void => {
    try {
      sessionStorage.clear()
    } catch (error) {
      console.error('Cache clear all error:', error)
    }
  },

  /**
   * Check if cache exists and is valid
   */
  has: (key: string): boolean => {
    return cache.get(key) !== null
  },
}

// Cache keys
export const CACHE_KEYS = {
  PROJECTS: 'projects_cache',
  PROJECTS_PAGE: 'projects_page_cache',
}
